"use client";

import { useCallback, useEffect } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";

import { useLanguage } from "../../context/LanguageContext";
import type { NavSectionId } from "../../lib/navigationTypes";
import { getSectionRoute } from "../../lib/navigationRoutes";
import { Header } from "../Header";
import { HeroSection } from "../sections/HeroSection";
import SiteFooter from "../SiteFooter";

const legacyHashSections: Array<Exclude<NavSectionId, "home">> = [
  "roles",
  "approach",
  "expertise",
  "about",
  "contact"
];

export default function MainPage(): JSX.Element {
  const { t, locale } = useLanguage();
  const direction = t.common.dir;
  const language = t.common.lang;
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hash = window.location.hash.replace("#", "").trim();
    if (!hash) return;

    if (hash === "home") {
      router.replace(getSectionRoute(locale, "home"));
      return;
    }

    if (legacyHashSections.includes(hash as (typeof legacyHashSections)[number])) {
      router.replace(getSectionRoute(locale, hash as NavSectionId));
    }
  }, [locale, router]);

  const handleNavigate = useCallback(
    (id: NavSectionId) => {
      router.push(getSectionRoute(locale, id));
    },
    [locale, router]
  );

  return (
    <div dir={direction} lang={language}>
      <Header />
      <main className="relative isolate space-y-8 overflow-hidden sm:space-y-10 lg:space-y-12">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-24 right-[-7rem] h-[340px] w-[520px] opacity-[0.04] dark:opacity-[0.06] rtl:right-auto rtl:left-[-7rem]">
            <Image
              src="/illustrations/signals/signals-wave.svg"
              alt=""
              width={800}
              height={600}
              className="h-full w-full object-contain"
              loading="lazy"
              aria-hidden="true"
            />
          </div>
        </div>
        <HeroSection onNavigate={handleNavigate} />
      </main>
      <SiteFooter />
    </div>
  );
}
